import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/ThemeContext";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

const BATCHES = ["Morning", "Evening", "Weekend"];
const SOURCES = ["Google", "Facebook", "Friend", "Hoarding", "Other"];

export default function Admission() {
  const { data } = useSite();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ fullName: "", mobile: "", email: "", courseInterest: "", batchTime: "Morning", source: "Google" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const c = params.get("course");
    if (c) setForm((f) => ({ ...f, courseInterest: c }));
  }, [params]);

  if (!data) return null;
  const courses = data.courses || [];
  const faqs = data.content.faqs || [];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.mobile.trim() || !form.courseInterest) {
      toast.error("Please fill name, mobile and course interest.");
      return;
    }
    setLoading(true);
    try {
      const { data: res } = await api.post("/leads", form);
      setSubmitted(true);
      toast.success(res.message);
    } catch {
      toast.error("Something went wrong. Please try again or call 9272980098.");
    }
    setLoading(false);
  };

  const inputCls = "w-full px-4 py-3.5 rounded-xl bg-surface border border-c text-t outline-none focus:border-primary-c transition-colors";

  return (
    <div className="relative z-10 pt-28">
      <section className="mx-auto max-w-6xl px-5 py-12" data-testid="admission-section" id="admission-form">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-primary-c">Admission</span>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-t mt-4">Start Your Learning Journey.</h1>
          <p className="text-muted-c text-lg mt-5 max-w-2xl">Fill in your details and our team will reach out within 24 hours.</p>
        </Reveal>

        <div className="grid gap-8 lg:grid-cols-12 mt-12">
          <div className="lg:col-span-7">
            <Reveal>
              {submitted ? (
                <div className="glass rounded-3xl p-10 text-center" data-testid="admission-success">
                  <CheckCircle2 className="text-primary-c mx-auto mb-5" size={56} />
                  <h2 className="font-display font-bold text-2xl text-t">Thank you!</h2>
                  <p className="text-muted-c mt-3">Our team will contact you within 24 hours.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ fullName: "", mobile: "", email: "", courseInterest: "", batchTime: "Morning", source: "Google" }); }}
                    className="btn-ghost px-6 py-3 mt-7" data-testid="submit-another">Submit another enquiry</button>
                </div>
              ) : (
                <form onSubmit={submit} className="glass rounded-3xl p-7 sm:p-9 space-y-5" data-testid="admission-form">
                  <div>
                    <label className="text-sm font-semibold text-t mb-2 block">Full Name *</label>
                    <input value={form.fullName} onChange={(e) => set("fullName", e.target.value)} className={inputCls} placeholder="Your full name" data-testid="input-fullname" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-semibold text-t mb-2 block">Mobile Number *</label>
                      <input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className={inputCls} placeholder="10-digit mobile" inputMode="numeric" data-testid="input-mobile" />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-t mb-2 block">Email (optional)</label>
                      <input value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@email.com" data-testid="input-email" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-t mb-2 block">Course Interest *</label>
                    <div className="relative">
                      <select value={form.courseInterest} onChange={(e) => set("courseInterest", e.target.value)} className={`${inputCls} appearance-none pr-10`} data-testid="select-course">
                        <option value="">Select a course</option>
                        {courses.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-c pointer-events-none" size={18} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-semibold text-t mb-2 block">Preferred Batch Time</label>
                      <div className="relative">
                        <select value={form.batchTime} onChange={(e) => set("batchTime", e.target.value)} className={`${inputCls} appearance-none pr-10`} data-testid="select-batch">
                          {BATCHES.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-c pointer-events-none" size={18} />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-t mb-2 block">How did you hear about us?</label>
                      <div className="relative">
                        <select value={form.source} onChange={(e) => set("source", e.target.value)} className={`${inputCls} appearance-none pr-10`} data-testid="select-source">
                          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-c pointer-events-none" size={18} />
                      </div>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-base" data-testid="submit-admission">
                    {loading ? "Submitting…" : "Submit Enquiry"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <h2 className="font-display font-bold text-2xl text-t mb-5">Frequently Asked</h2>
              <div className="space-y-3" data-testid="faq-list">
                {faqs.map((f, i) => (
                  <div key={i} className="glass rounded-2xl overflow-hidden">
                    <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 p-5 text-left" data-testid={`faq-${i}`}>
                      <span className="font-semibold text-t text-sm">{f.q}</span>
                      <ChevronDown className={`text-primary-c shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} size={18} />
                    </button>
                    {openFaq === i && <div className="px-5 pb-5 text-sm text-muted-c leading-relaxed">{f.a}</div>}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
